<template>
  <v-card
    class="ma-0 pa-0 pt-2 px-1 tax-books-card"
    width="100%"
    style="height: calc(100vh - 108px)"
    elevation="2"
  >
    <v-app-bar flat class="my-0 mb-2 py-1 px-2 tax-books-header" dense height="30">
      <v-toolbar-title>{{ $t($route.name) }}</v-toolbar-title>
      <v-spacer />
      <v-btn
        color="primary"
        outlined
        class="mt-4"
        height="32"
        :loading="loading"
        @click="getItems"
      >
        {{ $t("getReport") }}
      </v-btn>
    </v-app-bar>
    <v-progress-linear
      :active="loading"
      :indeterminate="loading"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-container class="tax-books-filters mt-1">
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center">
            <span class="me-2 text-body-1">{{ $t("financialYear") }}</span>
            <v-divider class="flex-grow-1" />
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="3" sm="3" class="mb-0 pb-0 tax-books-field-col">
          <autocomplete
            v-model="fromFinancialYear"
            :title="$t('fromFinancialYear')"
            type="year"
            :value="fromFinancialYear"
          />
        </v-col>
        <v-col cols="12" md="3" sm="3" class="mb-0 pb-0 tax-books-field-col">
          <autocomplete
            v-model="toFinancialYear"
            :title="$t('toFinancialYear')"
            type="year"
            :value="toFinancialYear"
          />
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col cols="12">
          <div class="d-flex align-center">
            <span class="me-2 text-body-1">{{ $t("filter") }}</span>
            <v-divider class="flex-grow-1" />
          </div>
        </v-col>
      </v-row>
      <v-row class="mb-2">
        <v-col cols="12" md="3" sm="3" class="mb-0 pb-0 tax-books-field-col">
          <tag v-model="tag" :solo="false" :input="tag" :height="25" />
        </v-col>
        <v-col cols="12" md="3" sm="3" class="mb-0">
          <v-select
            v-model="filterType"
            :label="$t('filter')"
            :items="filterTypeItems"
            height="20"
            dense
            outlined
            hide-details
            class="mb-1 dense"
            item-value="value"
            item-text="text"
          />
        </v-col>
        <v-col v-show="filterType == 'month'" cols="12" md="3" sm="3" class="mb-0">
          <v-text-field
            id="fromIdMonth"
            v-model="fromMonth"
            :label="$t('from') + ' ' + $t('month')"
            height="20"
            outlined
            dense
            hide-details
            class="mb-1 dense"
            clearable
          />
          <date-picker
            v-model="fromMonth"
            element="fromIdMonth"
            locale="fa,en"
            auto-submit
            type="year-month"
            format="jYYYY/jMM"
            :min="fromFinancialYear ? fromFinancialYear.start_date : null"
            :max="toFinancialYear ? toFinancialYear.end_date : null"
            simple
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
          />
        </v-col>
        <v-col v-show="filterType == 'month'" cols="12" md="3" sm="3" class="mb-0">
          <v-text-field
            id="toIdMonth"
            v-model="toMonth"
            :label="$t('until') + ' ' + $t('month')"
            height="20"
            outlined
            dense
            hide-details
            class="mb-1 dense"
            clearable
          />
          <date-picker
            v-model="toMonth"
            element="toIdMonth"
            locale="fa,en"
            auto-submit
            type="year-month"
            format="jYYYY/jMM"
            :min="fromFinancialYear ? fromFinancialYear.start_date : null"
            :max="toFinancialYear ? toFinancialYear.end_date : null"
            simple
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
          />
        </v-col>
        <v-col v-show="filterType == 'date'" cols="12" md="3" sm="3" class="mb-0">
          <v-text-field
            id="fromId"
            v-model="fromDate"
            :label="$t('from') + ' ' + $t('date')"
            height="20"
            outlined
            dense
            hide-details
            class="mb-1 dense"
            clearable
          />
          <date-picker
            v-model="fromDate"
            element="fromId"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            auto-submit
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
            @close="setDate"
          />
        </v-col>
        <v-col v-show="filterType == 'date'" cols="12" md="3" sm="3" class="mb-0">
          <v-text-field
            id="toId"
            v-model="toDate"
            :label="$t('until') + ' ' + $t('date')"
            height="20"
            outlined
            dense
            hide-details
            class="mb-1 dense"
            clearable
          />
          <date-picker
            v-model="toDate"
            element="toId"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            :min="fromDate"
            auto-submit
            :color="
              this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light']
                .primary
            "
            @close="setDate"
          />
        </v-col>
        <v-col cols="12" md="3" sm="3" class="mb-0 pb-0 tax-books-field-col">
          <autocomplete
            v-model="branch"
            title="branch"
            type="branch"
            :filter="true"
            :multiple="true"
          />
        </v-col>
        <v-col cols="12" md="3" sm="3" class="mb-0">
          <v-select
            v-model="reportDateType"
            :label="$t('report_type')"
            :items="reportDateTypeArray"
            height="20"
            dense
            outlined
            hide-details
            clearable
            class="mb-1 dense"
            item-value="value"
            item-text="text"
          />
        </v-col>
        <v-col cols="12" md="3" sm="3" class="mb-0">
          <v-select
            v-model="levelreportDateType"
            :label="$t('Level_reporting_doc')"
            :items="reportDateTypeWithGivenArray"
            height="20"
            dense
            outlined
            hide-details
            clearable
            class="mb-1 dense"
            item-value="value"
            item-text="text"
          />
        </v-col>
      </v-row>
    </v-container>
    <!--  <v-row class="mx-0">
      <v-col
        class="pb-2 px-2"
        cols="12"
      >
        <print
          :data-prop="data"
          :report-prop="reportName"
          :resource-prop="reportName"
        />
      </v-col>
    </v-row> -->
    <v-row />
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="deletDialog"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn icon class="close-hover" @click="deletDialog = false">
            <v-icon color="grey" large> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img class="mx-auto" width="200" src="@/assets/stop.svg" />
        </v-col>
        <v-col class="mt-3 pr-7">
          <p class="mx-auto font-weight-medium">
            {{ $t("are-you-sure-delete-this-case") }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn v-if="selected.length > 0" color="primary">
                {{ $t("remove") }}
              </v-btn>
              <v-btn v-if="selected.length == 0" color="primary">
                {{ $t("remove") }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="deletDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--    upload dialog-->
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : ''"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import General from "../../../../Mixins/general";
// import { AgGridVue } from 'ag-grid-vue'
import AG_GRID_LOCALE_EN from "../../../../locales/fa.json";
import print from "./../../components/general/printViewer/printViewer";
export default {
  name: "AccountingBalance",
  components: {
    print,
    // AgGridVue,
  },
  mixins: [General],
  props: [],
  data: () => ({
    selected: [],
    filterType: "month",
    filterTypeItems: null,
    fromFinancialYear: null,
    toFinancialYear: null,
    remain: "debtorCreditor",
    remainItems: [],
    year: null,
    balance: "4columns",
    fromMonthDisplay: null,
    documentType: null,
    totalAccount: null,
    balanceItems: null,
    category: null,
    integratorType: null,
    rangeDate: null,
    registerar: null,
    attractionMethod: null,
    toDate: null,
    fromDate: null,
    fromMonth: null,
    fromMonthItems: null,
    toMonthItems: null,
    toMonth: null,
    printDialog: false,
    uploadDialog: false,
    accountDialog: false,
    deletDialog: false,
    filter: false,
    financialStatus: null,
    customerStatus: null,
    status: "active",
    taskindex: 0,
    lead: null,
    refId: null,
    taskItem: null,
    model: null,
    from: 1,
    taskDialog: null,
    taskDialogList: null,
    showSelect: null,
    tag: null,
    temp: null,
    sellStatus: null,
    items: [],
    sortBy: [],
    data: null,
    dialog: null,
    timer: 0,
    totalItems: 0,
    snakbar: false,
    snakbarText: null,
    loading: false,
    options: {
      itemsPerPage: 5,
    },
    deleteItem: null,
    headers: null,
    personnel: null,
    search: null,
    renderTable: null,
    selectedHeaders: [],
    columnDefs: null,
    rowData: null,
    gridApi: null,
    localeText: null,
    reportName: null,
    priorityItems: [],
    priority: "total",
    branch: null,
    reportDateType: null,
    levelreportDateType: null,
    reportDateTypeArray: null,
    reportDateTypeWithGivenArray: null,
  }),
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    showHeaders() {
      var app = this;
      return this.headers.filter(function (s) {
        return app.selectedHeaders.includes(s);
      });
    },
    // totals: function () {
    // var app = this
    // app.excelItems = []
    // const totals = this.items.reduce((acc, d, index) => {
    //   acc.amount += parseFloat((d.remain ? d.remain : 0))
    //   app.excelItems.push({
    //     row: +index + +1,
    //     // name: String(d.name),
    //     // category: (d.category ? d.category.name : ''),
    //     // phone: (d.phone ? String(d.phone) : ''),
    //   })
    //   return acc
    // }, {
    //   amount: 0,
    // })
    // app.renderTable = true
    // return totals
    // },
  },
  watch: {
    // search () {
    //   var app = this
    //   app.loading = true
    //   clearTimeout(this.timer)
    //   this.timer = setTimeout(function () {
    //     app.getItems()
    //   }, 1000)
    // },
  },
  created() {
    this.localeText = AG_GRID_LOCALE_EN;
  },
  activated() {
    // this.getItems()
  },
  beforeMount() {
    this.balanceItems = [
      {
        value: "2columns",
        text: this.$t("2columns"),
      },
      {
        value: "4columns",
        text: this.$t("4columns"),
      },
      {
        value: "6columns",
        text: this.$t("6columns"),
      },
      {
        value: "8columns",
        text: this.$t("8columns"),
      },
    ];
    this.priorityItems = [
      {
        value: "total",
        text: this.$t("total"),
      },
      {
        value: "given",
        text: this.$t("given"),
      },
      {
        value: "detail",
        text: this.$t("detail"),
      },
      {
        value: "totalGivenDetail",
        text: this.$t("total") + "/" + this.$t("given") + "/" + this.$t("detail"),
      },
      {
        value: "totalGiven",
        text: this.$t("total") + "/" + this.$t("given"),
      },
      {
        value: "givenDetail",
        text: this.$t("given") + "/" + this.$t("detail"),
      },
    ];
    this.remainItems = [
      {
        value: "debtorCreditor",
        text: this.$t("debtor") + " " + this.$t("creditor"),
      },
      {
        value: "debtor",
        text: this.$t("debtor"),
      },
      {
        value: "creditor",
        text: this.$t("creditor"),
      },
      {
        value: "zero",
        text: this.$t("zero"),
      },
    ];
    this.filterTypeItems = [
      {
        value: "month",
        text: this.$t("month"),
      },
      {
        value: "date",
        text: this.$t("date"),
      },
    ];
    this.reportDateTypeArray = [
      { value: "doc", text: "به ازای هر سند" },
      { value: "daily", text: "تجمیع روزانه" },
      { value: "monthly", text: "تجمیع ماهانه" },
    ];
    this.reportDateTypeWithGivenArray = [
      { value: "doc", text: "به ازای هر سند" },
      { value: "given", text: "معین" },
      { value: "total", text: "کل" },
    ];
  },
  mounted() {
    this.headersMap = this.headers;
    this.selectedHeaders = this.headers;
    this.fromFinancialYear = this.$store.state.financialYear;
    this.toFinancialYear = this.$store.state.financialYear;
  },
  methods: {
    edit(item) {
      this.$router.push({ name: "customerEdit", params: { id: item.id } });
    },
    getItems() {
      const app = this;
      app.loading = true;

      app.$axios
        .get(this.$endpoint + "accounting/taxBooks", {
          params: {
            branch: app.branch,
            date_type:
              !app.reportDateType || app.reportDateType === "null"
                ? null
                : app.reportDateType,
            group_type: app.levelreportDateType || null,
            fromFinancialYear: app.fromFinancialYear ? app.fromFinancialYear.id : null,
            toFinancialYear: app.toFinancialYear ? app.toFinancialYear.id : null,
            fromMonth: app.fromMonth,
            toMonth: app.toMonth,
            fromDate: app.fromDate,
            toDate: app.toDate,
            sortBy: app.sortBy,
            options: app.options,
            tag: app.tag,
          },
          responseType: "arraybuffer",
        })
        .then((response) => {
          // create a blob from response
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          // create a link element
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          // set file name
          link.download = "taxBooks.xlsx";

          // trigger download
          link.click();

          // cleanup
          window.URL.revokeObjectURL(link.href);
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => {
          app.loading = false;
        });
    },
    onFilterTextBoxChanged() {
      this.gridApi.setQuickFilter(document.getElementById("filter-text-box").value);
    },
    onBtExport() {
      this.gridApi.exportDataAsExcel();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      // this.gridColumnApi = params.columnApi
      //
      // const updateData = (data) => {
      //   this.rowData = data
      // }
      //
      // fetch('https://www.ag-grid.com/example-assets/small-olympic-winners.json')
      //   .then((resp) => resp.json())
      //   .then((data) => updateData(data))
    },
  },
};
</script>
<style scoped>
.tax-books-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.tax-books-header {
  flex: 0 0 auto;
  background: #fff;
}
.tax-books-filters {
  flex: 0 0 auto;
}
.tax-books-field-col {
  height: 55px;
}
::v-deep
  .tax-books-filters
  .v-text-field.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 36px !important;
}
::v-deep
  .tax-books-filters
  .v-select.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 36px !important;
}
::v-deep .tax-books-filters .v-text-field--outlined .v-label {
  top: 10px !important;
}
::v-deep .tax-books-filters .v-input--is-label-active .v-label {
  transform: translateY(-16px) scale(0.75) !important;
}
.ag-theme-alpine {
  --ag-grid-size: 3px !important;
  --ag-list-item-height: 20px !important;
}
.v-badge__badge {
  height: 16px !important;
  min-width: 16px !important;
  padding: 4px 6px !important;
}
.v-data-table > .v-data-table__wrapper .v-data-table__mobile-row {
  height: initial;
  min-height: 32px !important;
}
</style>
